import axios from 'axios';
import {get} from 'lodash';
import {EventBus} from '@/plugins/eventBus';
import * as types from '../mutation-types';
import {performApiRequest} from '@/store/helpers/vuex-helpers';
import type {DocumentsMeta, OrderDocumentType, OrderLine, OrderType} from '@/types/order';
import {d} from '@/utils/consoleHelper.ts';
import {handleApiError} from '@/utils/errorHandler.ts';
import type {SortItem} from '@/store/modules/booking.ts';
import {createOrderUrlQueryString} from '@/utils/urls.ts';

type DocumentQueryOptions = {
    page?: number,
    sortDesc?: string | boolean | boolean[];
    itemsPerPage?: number,
    searchTerm?: string,
    searchTopic?: string,
    exporting?: boolean,
    providers?: string[],
    statuses?: string[],
    filter?: any,
    sortBy?: SortItem[],
    document_type?: string,
}

export type vTableOptions = {
    groupBy: string[],
    page: number,
    itemsPerPage: number,
    sortBy: { key: string, order: string }[],
    search: string | undefined,
}

export interface OrderState {
    loading: Record<string, boolean>;
    orders: OrderType[];
    order: OrderType | null;
    documents: OrderDocumentType[];
    documentsMeta: DocumentsMeta;
    orderDocumentsTableOptions: vTableOptions;
    document: OrderDocumentType | null;
    downloadStatus: any;
    availability: any;
    currentWorkingPricingWizardOrderLine: OrderLine | null;
    dialogs: {
        pricingWizard: boolean;
    };
    lastCreatedQuoteDocument: OrderDocumentType | null;
}

export interface UpdateOrderPayload {
    orderUuid: string;
    attributes?: Record<string, any>;
}

const state: OrderState = {
    loading: {
        orders: false,
        newOrder: false,
        update: false,
        cancelOrder: false,
        reserving: false,
        concept: false,
        deleteOrderDocument: false,
        updateLine: false,
        addLine: false,
        removeLine: false,
        holdDeposit: false,
        addCustomerToOrder: false,
        removeCustomer: false,
        getDocuments: false,
    },
    dialogs: {
        pricingWizard: false,
    },
    currentWorkingPricingWizardOrderLine: null,
    orders: [],
    order: null,
    documents: [],
    documentsMeta: {
        current_page: 1,
        from: 1,
        last_page: 1,
        links: [],
        path: '',
        per_page: 25,
        to: 1,
        total: 0,
    },
    orderDocumentsTableOptions: {
        groupBy: [],
        page: 1,
        itemsPerPage: 25,
        sortBy: [{'key': 'created_at', 'order': 'desc'}],
        search: undefined,
    },
    document: null,
    downloadStatus: null,
    availability: {},
    lastCreatedQuoteDocument: null,
};

interface AddLinePayload {
    orderUuid: string;
    line: OrderLine;
}

const getters = {
    orders: (state: OrderState) => state.orders,
    order: (state: OrderState) => state.order,
    documents: (state: OrderState) => state.documents,
    documentsMeta: (state: OrderState) => state.documentsMeta,
    orderDocumentsTableOptions: (state: OrderState) => state.orderDocumentsTableOptions,
    document: (state: OrderState) => state.document,
    loading: (state: OrderState) => state.loading,
    downloadStatus: (state: OrderState) => state.downloadStatus,
    availability: (state: OrderState) => state.availability,
    orderDialogs: (state: OrderState) => state.dialogs,
    currentWorkingPricingWizardOrderLine: (state: OrderState) => state.currentWorkingPricingWizardOrderLine,
};

const mutations = {
    [types.SET_ORDERS_LOADING](state: OrderState, loading: { subject: string, loading: boolean }) {
        state.loading[loading.subject] = loading.loading;
    },
    [types.SET_ORDER](state: OrderState, order: OrderType) {
        d.badge('SET_ORDER');
        state.order = order;
    },
    [types.UPDATE_ORDER_LINE](state: OrderState, line: OrderLine) {
        if (!state.order) return;

        state.order.lines = state.order.lines.map((l) =>
            l.uuid === line.uuid ? line : l
        );
    },
    [types.SET_ORDER_DOCUMENTS](state: OrderState, documents: OrderDocumentType[]) {
        state.documents = documents;
    },
    [types.SET_ORDER_DOCUMENTS_META](state: OrderState, meta: DocumentsMeta) {
        state.documentsMeta = meta;
    },
    [types.SET_ORDER_DOCUMENTS_TABLE_OPTIONS](state: OrderState, payload: vTableOptions) {
        state.orderDocumentsTableOptions = payload;
    },
    [types.SET_ORDER_DOCUMENT](state: OrderState, document: OrderDocumentType) {
        state.document = document;
    },
    [types.SET_ORDER_DOCUMENT_DOWNLOAD_STATUS](state: OrderState, status: {
        document: OrderDocumentType,
        status: object
    }) {
        state.downloadStatus = status;
    },
    [types.SET_ORDER_AVAILABILITY](state: OrderState, availability) {
        state.availability = availability;
    },
    [types.SET_OPEN_PRICING_WIZARD_DIALOG](state: OrderState, data: { open: boolean, lineItem: OrderLine }) {
        state.currentWorkingPricingWizardOrderLine = data.lineItem;
        state.dialogs.pricingWizard = data.open;
    },
    SET_LAST_CREATED_QUOTE_DOCUMENT(state: OrderState, document: OrderDocumentType | null) {
        state.lastCreatedQuoteDocument = document;
    },
};

const actions = {

    async newOrder({commit}) {
        try {
            const responseData = await performApiRequest(
                commit,
                'SET_ORDERS_LOADING',
                'newOrder',
                () => axios.get('/v1/orders/drafts/latest')
            );

            commit('SET_ORDER', responseData.order);
            EventBus.$emit('get-new-order-success', responseData.order);
        } catch (error) {
            d.error('Error creating new order:', error);
        }
    },

    /**
     * Get order
     */
    async getOrder({commit}, orderUuid: string): Promise<void> {
        try {
            const responseData = await performApiRequest(
                commit,
                'SET_ORDERS_LOADING',
                'getOrder',
                () => axios.get(`/v1/orders/${orderUuid}`)
            );

            commit('SET_ORDER', responseData.order);
        } catch (error) {
            handleApiError(error);
        }
    },

    /**
     * Update order
     */
    async update({commit}, {orderUuid, attributes = {}}: UpdateOrderPayload) {
        try {
            const responseData = await performApiRequest(
                commit,
                'SET_ORDERS_LOADING',
                'update',
                () => axios.put(`/v1/orders/${orderUuid}`, {attributes})
            );
            const {order} = responseData;
            commit('SET_ORDER', order);
            EventBus.$emit('update-order-success', order);
        } catch (error) {
            handleApiError(error);
        }
    },

    async makeCustomerPrimary({commit}, {orderUuid, r_id}) {
        try {
            const responseData = await performApiRequest(
                commit,
                'SET_ORDERS_LOADING',
                'update',
                () => axios.patch(`/v1/orders/${orderUuid}/customers/${r_id}/primary`)
            );

            commit(types.SET_ORDER, responseData.order);
            EventBus.$emit('update-order-success', responseData.order);
        } catch (error) {
            d.error('Error creating new order:', error);
        }
    },


    async removeCustomer({commit}, {orderUuid, r_id}) {
        const responseData = await performApiRequest(
            commit,
            'SET_ORDERS_LOADING',
            'removeCustomer',
            () => axios.delete(`/v1/orders/${orderUuid}/customers/${r_id}`)
        );

        commit('SET_ORDER', responseData.order);
        EventBus.$emit('update-order-success', responseData.order);
    },

    async addCustomerToOrder({commit}, {orderUuid, r_id}) {
        const responseData = await performApiRequest(
            commit,
            'SET_ORDERS_LOADING',
            'addCustomerToOrder',
            () => axios.post(`/v1/orders/${orderUuid}/customers`, {r_id})
        );

        commit('SET_ORDER', responseData.order);
        EventBus.$emit('update-order-success', responseData.order);
        EventBus.$emit('add-customer-to-order-success', responseData.order);
    },


    /**
     * Reserve a new order.
     */
    async reserve({commit}, orderUuid: string) {
        const responseData = await performApiRequest(
            commit,
            'SET_ORDERS_LOADING',
            'reserving',
            () => axios.post(`/v1/orders/${orderUuid}/reservations`)
        );

        commit(types.SET_ORDER, responseData.order);
        EventBus.$emit('reserve-order-success', responseData);
    },


    /**
     * Create a new order concept.
     */
    async concept({commit}, orderUuid: string) {
        try {
            const responseData = await performApiRequest(
                commit,
                'SET_ORDERS_LOADING',
                'concept',
                () => axios.post(`/v1/orders/${orderUuid}/draft`)
            );
            commit('SET_ORDER', responseData.order);
            EventBus.$emit('reserve-order-success', responseData);
        } catch (error) {
            handleApiError(error);
        }
    },


    /**
     * Check the availability of a new order.
     */
    async availability({commit}, orderUuid: string) {
        const responseData = await performApiRequest(
            commit,
            'SET_ORDERS_LOADING',
            'availability',
            () => axios.get(`/v1/orders/${orderUuid}/availability`)
        );

        commit(types.SET_ORDER_AVAILABILITY, responseData.available || []);
    },


    /**
     * Hold the deposit of an order
     */
    async holdOrderDeposit({commit}, {orderUuid, attributes}) {
        await performApiRequest(
            commit,
            'SET_ORDERS_LOADING',
            'holdDeposit',
            () => axios.post(`/v1/orders/${orderUuid}/deposits/hold`, attributes)
        );

        EventBus.$emit('hold-order-deposit-success');
    },


    /**
     * Create a new quote
     */
    async createNewQuote({commit}, attributes: { attributes: { order_id?: string } }) {
        const responseData = await performApiRequest(
            commit,
            'SET_ORDERS_LOADING',
            'update',
            () => axios.post('/v1/quotes', attributes)
        );

        commit('SET_LAST_CREATED_QUOTE_DOCUMENT', responseData?.document);
    },

    async createNewOrderDocument({commit}, attributes: { type: string, order_id: string }) {
        const responseData = await performApiRequest(
            commit,
            'SET_ORDERS_LOADING',
            'update',
            () => axios.post('/v1/order-documents', attributes)
        );

        commit('SET_LAST_CREATED_QUOTE_DOCUMENT', responseData?.document);
    },


    async getAllOrderDocuments({commit}, options: DocumentQueryOptions = {}) {

        const data = {
            page: options.page || 1,
            sortDesc: options.sortDesc || false,
            itemsPerPage: options.itemsPerPage || 25,
            searchTerm: options.searchTerm || '',
            searchTopic: options.searchTopic || '',
            exporting: options.exporting || false,
            document_type: options.document_type //'Document::Invoice',
        }

        const query = createOrderUrlQueryString(data, true);

        const responseData = await performApiRequest(
            commit,
            'SET_ORDERS_LOADING',
            'update',
            () => axios.get(`/v1/order-documents${query}`)
        );

        commit(types.SET_ORDER_DOCUMENTS, responseData?.data);
        commit(types.SET_ORDER_DOCUMENTS_META, responseData?.meta);
    },


    /**
     *Get the orders documents (rental agreement envelope, invoice, etc)
     */
    async getOrderDocuments({commit}, orderUuid: string) {
        const responseData = await performApiRequest(
            commit,
            'SET_ORDERS_LOADING',
            'getDocuments',
            () => axios.get(`/v1/orders/${orderUuid}/documents`)
        );

        commit('SET_ORDER_DOCUMENTS', responseData?.data);
    },

    async getOrderDocument({commit}, documentUuid: string) {
        try {
            const responseData = await performApiRequest(
                commit,
                'SET_ORDERS_LOADING',
                'getDocuments',
                () => axios.get(`/v1/order-documents/${documentUuid}`)
            );

            commit(types.SET_ORDER_DOCUMENT, responseData?.document);

            return responseData?.document;
        } catch (error) {
            handleApiError(error);
        }
    },

    updateOrderDocument({commit}, {documentUuid, attributes}) {
        commit('SET_ORDERS_LOADING', {subject: 'update', loading: true});

        axios
            .put(`/v1/order-documents/${documentUuid}`, {attributes})
            .then((response) => {
                commit('SET_ORDER_DOCUMENT', get(response, 'data.document'));
                EventBus.$emit('update-order-document-success', get(response, 'data.document'));
            })
            .catch((error) => {
                EventBus.$emit('update-order-document-error', error.response);
                handleApiError(error);
            })
            .finally(() => {
                commit('SET_ORDERS_LOADING', {subject: 'update', loading: false});
            });
    },

    async deleteOrderDocument({commit}, documentUuid: string) {
        console.dir('deleteOrderDocument');
        return await performApiRequest(
            commit,
            'SET_ORDERS_LOADING',
            'deleteOrderDocument',
            () => axios.delete(`/v1/order-documents/${documentUuid}`)
        );
    },

    async downloadOrderDocument({commit}, documentUuid: string) {
        const responseData = await performApiRequest(
            commit,
            'SET_ORDERS_LOADING',
            'update',
            () => axios.get(`/v1/order-documents/${documentUuid}/download`)
        );

        commit(types.SET_ORDER_DOCUMENT_DOWNLOAD_STATUS, responseData);
        EventBus.$emit('start-order-document-download-success', responseData);
    },


    /**
     * Add a line item to an order
     */
    async addLine({commit}, {orderUuid, line}: AddLinePayload) {
        try {
            const responseData = await performApiRequest(
                commit,
                'SET_ORDERS_LOADING',
                'addLine',
                () => axios.post(`/v1/orders/${orderUuid}/line`, {line})
            );

            EventBus.$emit('add-order-line-success', responseData.order);
            commit('SET_ORDER', responseData.order);
        } catch (error) {
            handleApiError(error);
        }
    },

    async updateLine({commit}, {line, attributes}: { line: string, attributes: Record<string, any> }) {
        const responseData = await performApiRequest(
            commit,
            'SET_ORDERS_LOADING',
            'updateLine',
            () => axios.put(`/v1/line/${line}`, {attributes})
        );

        commit('SET_ORDER', responseData.order);
        EventBus.$emit('update-order-success', responseData.order);
    },

    async updateLines({commit}, lines: OrderLine[]) {
        await performApiRequest(
            commit,
            'SET_ORDERS_LOADING',
            'updateLine',
            () => axios.put('/v1/lines', {lines})
        );
    },


    async removeLine({commit}, line: string) {
        try {
            await performApiRequest(
                commit,
                'SET_ORDERS_LOADING',
                'removeLine',
                () => axios.delete(`/v1/line/${line}`)
            );
        } catch (error) {
            handleApiError(error);
        }
    },

    /**
     * Get a line item model (ex. location model)
     */
    getOrderItemModel({commit}, data: { model: string, id: string }) {
        commit('SET_ORDERS_LOADING', {subject: 'itemModel', loading: true});

        axios
            .get(`/v1/line-item-model/${data.model}/${data.id}`)
            .then((response) => {
                EventBus.$emit('get-order-line-model-success', get(response, 'data.model'));
            })
            .catch(() => {
            }).finally(() => {
            commit('SET_ORDERS_LOADING', {subject: 'itemModel', loading: false});
        });
    },


    /**
     * Cancel an order
     */
    async cancelOrder({commit}, orderUuid: string) {
        try {
            await performApiRequest(
                commit,
                'SET_ORDERS_LOADING',
                'cancelOrder',
                () => axios.post(`/v1/orders/${orderUuid}/cancel`)
            );

            EventBus.$emit('cancel-order-success');
        } catch (error) {
            handleApiError(error);
        }
    },
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
};
